/**
 * @module builder
 */
import type { TieredPackages, TieredPackageTierId } from 'paintscout'

/** low → mid → high, in display order. */
export const TIER_ORDER: TieredPackageTierId[] = ['low', 'mid', 'high']

/**
 * Fixed positional labels for each tier slot. Used as the row headings in the setup
 * wizard and as the fallback display name when a tier's customer-facing `name` is blank.
 */
export const TIER_LABELS: Record<TieredPackageTierId, string> = {
  low: 'Basic',
  mid: 'Standard',
  high: 'Premium'
}

/**
 * The tier the estimate as built always represents. The estimator prices one estimate, it is the
 * middle package, and Basic and Premium move up and down from it — so the base is fixed rather
 * than chosen.
 */
export const BASE_TIER_ID: TieredPackageTierId = 'mid'

/**
 * A blank tiered-packages config used to seed the drawer. The middle tier is the base (the estimate
 * as built) and the other two start level with it: without a product swap a package costs what the
 * estimate costs until the estimator overrides it. Tier `name`s start blank so the Package Name
 * input shows its placeholder and the UI falls back to the positional label (TIER_LABELS).
 */
export function getDefaultTieredPackages(): TieredPackages {
  const tier = (level: TieredPackages['tiers']['low']['level'], value = 0) => ({
    level,
    name: '',
    tagline: '',
    features: [] as string[],
    description: '',
    adjustment: { type: 'percentage' as const, value }
  })

  return {
    enabled: false,
    baseTierId: BASE_TIER_ID,
    selectedTierId: BASE_TIER_ID,
    recommendedTierId: BASE_TIER_ID,
    priceMode: 'percentage',
    priceDisplay: 'total',
    // Presentation belongs to the set, not the tier: one content type for all three cards.
    contentType: 'features',
    tiers: {
      low: tier('Basic'),
      mid: tier('Standard'),
      high: tier('Premium')
    }
  }
}
